
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { DollarSign, Target, FileSearch, Coins } from "lucide-react";
import { formatCurrency } from "@/lib/utils";
import { WorkspaceAnalytics } from "@/types/workspace";
import { AnimatedCounter } from "./AnimatedCounter";

interface MoneyFoundCardProps {
    analytics: WorkspaceAnalytics;
}

export function MoneyFoundCard({ analytics }: MoneyFoundCardProps) {
    // Calculate total money found from carrier overscope and missed items
    const totalMoneyFound = (analytics.totalCarrierOverscope || 0) + (analytics.totalMissedItems || 0);
    
    // Use potential commission from backend (already calculated as 10% of money found)
    const potentialCommission = analytics.potentialCommission || 0;

    return (
        <Card className="relative overflow-hidden border-0 bg-gradient-to-br from-amber-500/10 via-white to-amber-600/5 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1 h-full flex flex-col">
            <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-amber-500/20 to-transparent rounded-full -translate-x-8 -translate-y-8 blur-2xl" />
            <CardHeader className="pb-2 relative">
                <CardTitle className="text-sm font-medium flex items-center gap-2 text-gray-700">
                    <div className="p-2 bg-amber-500/20 rounded-lg">
                        <DollarSign className="h-4 w-4 text-amber-600" />
                    </div>
                    Total Money Found
                </CardTitle>
            </CardHeader>
            <CardContent className="relative space-y-2">
                <div className="text-2xl font-bold text-amber-700">
                    <AnimatedCounter
                        value={totalMoneyFound}
                        format={formatCurrency}
                    />
                </div>
                <div className="space-y-1 text-xs">
                    <div className="flex justify-between items-center group">
                        <span className="text-gray-500 flex items-center gap-1">
                            <Target className="h-3 w-3 opacity-50" />
                            Carrier overscope:
                        </span>
                        <span className="font-medium text-blue-600">
                            {formatCurrency(analytics.totalCarrierOverscope || 0)}
                        </span>
                    </div>
                    <div className="flex justify-between items-center group">
                        <span className="text-gray-500 flex items-center gap-1">
                            <FileSearch className="h-3 w-3 text-orange-500" />
                            Items contractor missed:
                        </span>
                        <span className="font-medium text-orange-600">
                            {formatCurrency(analytics.totalMissedItems || 0)}
                        </span>
                    </div>
                    <div className="border-t pt-1 flex justify-between items-center group border-gray-100">
                        <span className="text-gray-500 flex items-center gap-1">
                            <Coins className="h-3 w-3 text-amber-500 animate-pulse" />
                            Potential commission:
                        </span>
                        <span className="font-bold text-amber-600">
                            {formatCurrency(potentialCommission)}
                        </span>
                    </div>
                </div>
            </CardContent>
        </Card>
    );
}
